import {
  CSSProperties,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import JuisciLogo from "../../../assets/core/juisci-logo.svg?react";
import NavBackIcon from "../../../assets/icons/arrow-left-circle-duotone.svg?react";
import NavBackIconBlack from "../../../assets/icons/arrow-left-circle-fill.svg?react";
import NavOptionsIcon from "../../../assets/icons/dots-circle-red.svg?react";
import NavBellWhiteIcon from "../../../assets/icons/nav/nav-bell-white.svg?react";
import NavBellYellowIcon from "../../../assets/icons/nav/nav-bell-yellow.svg?react";
import NavLangIcon from "../../../assets/icons/nav/nav-lang-v2.svg?react";
import NavSettingsWhiteIcon from "../../../assets/icons/nav/nav-settings-white.svg?react";
import NavSettingsYellowIcon from "../../../assets/icons/nav/nav-settings-yellow.svg?react";
import NavShareIcon from "../../../assets/icons/nav/nav-share.svg?react";
import LanguagePanel from "../LanguagePanel";
import { useTranslation } from "react-i18next";
import { isDesktopLayout } from "../../../common/hooks/useIsDesktopView";
import { goBackInApp } from "../../../domains/app/utils/backNavigation";
import {
  openSettings,
  useSettingsSheet,
} from "../../../domains/app/stores/settingsSheetStore";

export function HeaderBoxAutoSafe({
  children,
  transparent,
  className,
}: {
  children?: ReactNode;
  transparent?: boolean;
  className?: HTMLAttributes<HTMLDivElement>["className"];
}) {
  return (
    <div
      id="header-box"
      className={`relative ${transparent ? "bg-transparent shadow-none" : "bg-white"} [&_hr]:border-secondary-bluegrey-2 ${className || ""}`}
    >
      <nav className="py-[11px] px-4 flex items-center justify-between bg-white shadow-[0px_4px_4px_0px_rgba(33,33,33,0.1)]">
        {children}
      </nav>
    </div>
  );
}

export function HeaderBoxTransparent({ children }: { children?: ReactNode }) {
  return (
    <div id="header-box" className="relative bg-transparent">
      <nav className="py-[11px] px-4 flex items-center justify-between bg-transparent">
        {children}
      </nav>
    </div>
  );
}

export function HeaderLinks({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex items-center gap-4 [&_a]:flex ${className || ""}`}>
      {children}
    </div>
  );
}

export function HeaderLogoTitleTagline({
  logo,
  title,
  tagline,
}: {
  logo: ReactNode;
  title: string;
  tagline: string;
}) {
  const { t } = useTranslation();
  const roomNameRef = useRef<HTMLSpanElement>(null);
  const [isTwoLines, setIsTwoLines] = useState(false);

  // CHECK: is there a better way of doing this?
  useEffect(() => {
    const roomNameElement = roomNameRef.current;
    if (roomNameElement) {
      // Get the width of the element with white-space: nowrap
      roomNameElement.style.whiteSpace = "nowrap";
      const singleLineHeight = roomNameElement.scrollHeight;

      // Now allow the element to wrap to calculate actual height
      roomNameElement.style.whiteSpace = "normal";
      const wrappedHeight = roomNameElement.scrollHeight;

      // Reset white-space back to its original setting
      roomNameElement.style.whiteSpace = "";

      // If the height increases when wrapping, it's more than one line
      const isOverflowing = wrappedHeight > singleLineHeight;
      setIsTwoLines(isOverflowing);
    }
  }, [title]);

  return (
    <div className="flex gap-2 items-center justify-center flex-1 mx-4 min-w-0">
      {logo}
      <div className="flex flex-col min-w-0 overflow-hidden">
        <span
          className={`font-dm-sans font-semibold overflow-hidden text-ellipsis pb-px ${
            isTwoLines
              ? "text-[10px] line-clamp-2"
              : "text-xs whitespace-nowrap"
          }`}
          ref={roomNameRef}
        >
          {title}
        </span>
        <span className="truncate   font-dm-sans text-[8px] font-normal">
          {t("By")} {tagline}
        </span>
      </div>
    </div>
  );
}

// NOTE: remove?
export function HeaderTitle({
  children,
  className,
  style,
  truncate,
}: {
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  truncate?: boolean;
}) {
  return (
    <div
      className={`mx-4 text-secondary-bluegrey-7 text-center text-base font-medium leading-[26px] tracking-[-0.16px] flex items-center gap-1 ${
        truncate === true ? "truncate  " : ""
      } ${className ?? ""}`}
      style={style}
    >
      {children}
    </div>
  );
}

export function HeaderTagline({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`text-secondary-bluegrey-7 text-center uppercase text-[6px] font-normal leading-[6px] tracking-[0.96px] ${className || ""}`}
    >
      {children}
    </div>
  );
}

export function HeaderLogo() {
  return (
    <div className="m-auto [&_svg]:block [&_svg]:h-[34px]">
      <JuisciLogo />
    </div>
  );
}

export function NavSettings() {
  const { pathname } = useLocation();
  // The settings sheet replaces the /settings route on mobile/native; desktop
  // web keeps the page route.
  const isSheetOpen = useSettingsSheet().open;

  if (!isDesktopLayout()) {
    return (
      <button type="button" onClick={openSettings} className="inline-flex">
        {isSheetOpen ? (
          <NavSettingsYellowIcon className="cursor-pointer" />
        ) : (
          <NavSettingsWhiteIcon className="cursor-pointer" />
        )}
      </button>
    );
  }

  return (
    <Link to="/settings">
      {pathname.startsWith("/settings") ? (
        <NavSettingsYellowIcon className="cursor-pointer" />
      ) : (
        <NavSettingsWhiteIcon className="cursor-pointer" />
      )}
    </Link>
  );
}

export function NavNotifications() {
  const { pathname } = useLocation();
  return (
    <Link to="/profile/notifications">
      {pathname.startsWith("/profile/notifications") ? (
        <NavBellYellowIcon className="cursor-pointer" />
      ) : (
        <NavBellWhiteIcon className="cursor-pointer" />
      )}
    </Link>
  );
}

export function NavBack({
  to,
  onClick,
  variant = "default",
}: {
  to?: string;
  onClick?: () => void;
  variant?: "default" | "black";
}) {
  const history = useHistory();
  const Icon = {
    default: NavBackIcon,
    black: NavBackIconBlack,
  }[variant];

  return (
    <Icon
      className="rtl-revert cursor-pointer"
      onClick={() => {
        if (to) {
          history.push(to);
        } else if (onClick) {
          onClick();
        } else {
          goBackInApp(history, "/discovery");
        }
      }}
    />
  );
}

export function NavOptions({ onClick }: { onClick?: () => void }) {
  return (
    <NavOptionsIcon
      className="cursor-pointer"
      onClick={() => onClick && onClick()}
    />
  );
}

export function NavLang({
  allowedLanguages,
}: {
  allowedLanguages?: string[];
} = {}) {
  const [isPanelVisible, setPanelVisible] = useState(false);

  return (
    <>
      <NavLangIcon
        className="cursor-pointer size-6 text-primary-black"
        onClick={() => {
          setPanelVisible(true);
        }}
      />
      <LanguagePanel
        isVisible={isPanelVisible}
        onClose={() => {
          setPanelVisible(false);
        }}
        allowedLanguages={allowedLanguages}
      />
    </>
  );
}

export function NavShare({ onClick }: { onClick?: () => void }) {
  return (
    <NavShareIcon
      className="rtl-revert cursor-pointer"
      onClick={() => onClick && onClick()}
    />
  );
}
